import React, { Fragment, useEffect, useState } from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { Text, Button, Box, Flex, Heading, Card } from 'rebass/styled-components';
import { useLazyQuery } from '@apollo/client';
import { IGetCalendarForMonthOutput } from '../../graphql/types/queries/appointment';
import { IGenerateCircleSoCallbackInput } from '../../graphql/types/mutations/community';
import { GET_CALENDAR_FOR_MONTH } from '../../graphql/queries';

const TimesWrapper = styled(Flex)`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  height: 100%;
`;

const TimesText = styled.span`
  display: block;
  font-weight: 300;
  font-size: 12px;

  @media screen and (max-width: 52em) {
    display: none;
  }
`;

const TimesIndicator = styled(Box)`
  display: block;
  background: rgb(188, 255, 235);
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-top: 4px;

  @media screen and (min-width: 52em) {
    display: none;
  }
`;

const DayMobile = styled.div`
  display: block;

  @media screen and (min-width: 52em) {
    display: none;
  }
`;

const DayFull = styled.span`
  display: block;
  color: #ccc;
  font-size: 14px;

  @media screen and (max-width: 52em) {
    display: none;
  }
`;

const AvailabilitiesLoadingErrorWrapper = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.75);
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

const AvailabilitiesLoadingErrorText = styled.div`
  margin-top: 20px;
  font-weight: 500;
  &.error {
    color: #f9a394;
  }
  &.title {
    font-weight: 700;
  }
`;

const ExpandedRow = styled.div`
  padding: 20px;
  border-width: 2px 0;
  border-color: #4467e2;
  border-style: solid;
  position: relative;
`;

const SlotButton = styled(Button)`
  margin: 6px;
  width: auto;
  border-width: 1px;
  font-size: 14px;
`;

export interface ICalendarGrid {
  selectedDate: string | null;
  setSelectedDate: (date: string | null) => void
  showTitle?: boolean
}

const Svg = styled.svg`
  
`

const Path = styled.path`
fill: #344f79;
`

const CalendarMonthButton = styled(Flex)`
  width: 24px;
  height: 24px;
  border-radius: 12px;
  border: 1px solid #F1F0F3;
  align-items: center;
  justify-content: center;
  background-color: white;
  cursor: pointer;
  margin-left: 12px;
  &:hover {
    background-color: #344f79;
    >${Svg} {
      >${Path}{
    fill: white;
  }

  }
  }
`
const MinimalCalendarGrid = ({ selectedDate, setSelectedDate, showTitle = true }: ICalendarGrid) => {
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date())
  const [getCalendar, { data: dataGetCalendar, loading: loadingGetCalendar, error: errorGetCalendar }] = useLazyQuery<IGetCalendarForMonthOutput, IGenerateCircleSoCallbackInput>(GET_CALENDAR_FOR_MONTH)

  useEffect(() => {
    getCalendar({
      variables: {
        date: moment().format('MM/DD/YYYY')
      }
    })
  }, [])

  useEffect(() => {
    getCalendar({
      variables: {
        date: moment(currentMonth).format('MM/DD/YYYY')
      }
    })
  }, [currentMonth])

  const renderHeader = () => {
    const dateFormat = 'MMMM, YYYY';

    return (
      <Flex p={3} alignItems={'center'}>
        <Text fontWeight={600} flex={1}>{moment(currentMonth).format(dateFormat)}</Text>
        <CalendarMonthButton onClick={prevMonth}>
          <Svg width="7" height="10" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <Path d="M6.29244 0.292444C6.19993 0.199741 6.09004 0.126193 5.96907 0.0760117C5.8481 0.0258302 5.71841 0 5.58744 0C5.45648 0 5.32679 0.0258302 5.20582 0.0760117C5.08485 0.126193 4.97496 0.199741 4.88244 0.292444L0.292444 4.88244C0.199741 4.97496 0.126193 5.08485 0.0760117 5.20582C0.0258302 5.32679 0 5.45648 0 5.58744C0 5.71841 0.0258302 5.8481 0.0760117 5.96907C0.126193 6.09004 0.199741 6.19993 0.292444 6.29244L4.88244 10.8824C4.97503 10.975 5.08494 11.0485 5.2059 11.0986C5.32687 11.1487 5.45651 11.1745 5.58744 11.1745C5.71837 11.1745 5.84802 11.1487 5.96899 11.0986C6.08995 11.0485 6.19986 10.975 6.29244 10.8824C6.38503 10.7899 6.45847 10.68 6.50857 10.559C6.55868 10.438 6.58447 10.3084 6.58447 10.1774C6.58447 10.0465 6.55868 9.91687 6.50857 9.7959C6.45847 9.67494 6.38503 9.56503 6.29244 9.47244L2.41244 5.58244L6.29244 1.70244C6.68244 1.31244 6.67244 0.672444 6.29244 0.292444Z" />
          </Svg>
        </CalendarMonthButton>
        <CalendarMonthButton onClick={nextMonth}>
          <Svg width="8" height="10" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <Path d="M1.29 0.709778C1.1973 0.802292 1.12375 0.91218 1.07357 1.03315C1.02339 1.15413 0.997559 1.28381 0.997559 1.41478C0.997559 1.54575 1.02339 1.67543 1.07357 1.7964C1.12375 1.91738 1.1973 2.02726 1.29 2.11978L5.17 5.99978L1.29 9.87978C1.19742 9.97236 1.12398 10.0823 1.07388 10.2032C1.02377 10.3242 0.997982 10.4538 0.997982 10.5848C0.997982 10.7157 1.02377 10.8454 1.07388 10.9663C1.12398 11.0873 1.19742 11.1972 1.29 11.2898C1.38258 11.3824 1.4925 11.4558 1.61346 11.5059C1.73442 11.556 1.86407 11.5818 1.995 11.5818C2.12593 11.5818 2.25558 11.556 2.37655 11.5059C2.49751 11.4558 2.60742 11.3824 2.7 11.2898L7.29 6.69978C7.38271 6.60726 7.45625 6.49738 7.50644 6.3764C7.55662 6.25543 7.58245 6.12575 7.58245 5.99478C7.58245 5.86381 7.55662 5.73413 7.50644 5.61315C7.45625 5.49218 7.38271 5.38229 7.29 5.28978L2.7 0.699778C2.32 0.319778 1.68 0.319778 1.29 0.709778Z" />
          </Svg>
        </CalendarMonthButton>

      </Flex>
      // <div className="header row flex-middle" style={{display: 'flex'}}>
      //   <div className="col col-start" style={{flex:1}}>
      //     <span>{moment(currentMonth).format(dateFormat)}</span>
      //   </div>
      //     <div className="icon" onClick={prevMonth}>
      //       {'<'}
      //     </div>
      //     <div className="icon">{'>'}</div>
      // </div>
    );
  };

  const renderDays = () => {
    const dateFormat = 'dd';
    const days = [];

    let startDate = moment(currentMonth).startOf('week');
    let startDateMob = moment(currentMonth).startOf('week');
    startDate.add(-1, 'days').format(dateFormat);
    startDateMob.add(-1, 'days').format('dd');
    for (let i = 0; i < 7; i++) {
      days.push(
        <div className="col col-center" key={i}>
          <DayFull>{startDate.add(1, 'days').format(dateFormat)}</DayFull>
        </div>
      );
    }

    return <div className="days row">{days}</div>;
  };

  const renderCells = () => {// (slots, loading, error, expandDay) => {
    const monthStart = moment(currentMonth).startOf('month');
    const monthEnd = moment(currentMonth).endOf('month');
    const startDate = moment(monthStart).startOf('week');
    const endDate = moment(monthEnd).endOf('week');

    const dateFormat = 'D';
    const rows = [];

    let days = [];
    let day = startDate;
    let formattedDate = '';

    // const counts=
    // dataGetCalendar && dataGetCalendar.getProviderCalendarForMonth && dataGetCalendar.getProviderCalendarForMonth.openSlots
    //   ? dataGetCalendar.getProviderCalendarForMonth.openSlots.reduce((p, c) => {
    //     const name = c.date;
    //     if (!p.hasOwnProperty(name)) {
    //       p[name] = 0;
    //     }
    //     p[name]++;
    //     return p;
    //   }, {})
    //   : [];

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        formattedDate = moment(day).format(dateFormat);
        const clone = moment(day)
          .format('MM/DD/YYYY')
          .toString();
        const openCount = dataGetCalendar && dataGetCalendar.getProviderCalendarForMonth && dataGetCalendar.getProviderCalendarForMonth.openSlots ?
          dataGetCalendar.getProviderCalendarForMonth.openSlots.find(x => x.date === clone)?.count || 0 : 0
        const bookedCount = dataGetCalendar && dataGetCalendar.getProviderCalendarForMonth && dataGetCalendar.getProviderCalendarForMonth.booked ?
          dataGetCalendar.getProviderCalendarForMonth.booked.find(x => x.date === clone)?.count || 0 : 0
        //   counts[moment(day).format('MM/DD/YYYY')];

        days.push(
          <Box
            className={`col cellMin 
            ${!moment(day).isSame(moment(monthStart), 'month')
                ? 'disabled' :
                moment(day).isSame(moment(selectedDate), 'day')
                  ? 'selected'
                  : moment(day).isBefore(moment(), 'day') ? 'past'
                    : ''
              }
              `}
            key={day.format()}
            onClick={e => {
              // if (!count || count < 1) return;
              onDateClick(clone);
            }}
          >
            <Box style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '36px',
              width: '36px',
              borderRadius: '20px',
              background: moment(day).isSame(moment(selectedDate), 'day') ? '#344f79' : bookedCount > 0 ? 'rgba(246, 162, 147, 0.23)' : 'white'
            }}>
              <span className="number">{formattedDate}</span>
            </Box>
            {/* <span className="bg">{formattedDate}</span> */}
            {/* <TimesWrapper flexDirection={['column-reverse', 'column', 'column']}>
              {openCount && openCount > 0 ? (
                <>
                  <TimesText>
                    {`${openCount} opening${openCount > 1 ? 's' : ''}`}
                  </TimesText>
                  <TimesIndicator />
                </>
              ) : null}
              {bookedCount > 0 ? (
                <>
                  <Box display={['none', 'initial', 'initial']} style={{ backgroundColor: '#B8D6F5', padding: '2px', borderRadius: '12px', width: 'calc(100% - 24px)' }} marginX={'12px'}>
                    <Text textAlign={'center'} fontSize='12px' color='#344f79 !important'>
                      {`${bookedCount} visit${bookedCount > 1 ? 's' : ''} >>`}
                    </Text>
                  </Box>
                  <Box display={['initial', 'none', 'none']} style={{ backgroundColor: '#B8D6F5', padding: '2px', borderRadius: '12px', width: 'calc(100% - 24px)' }} marginX={'12px'}>
                    <Text textAlign={'center'} fontSize='12px' color='#344f79 !important'>
                      {`${bookedCount}`}
                    </Text>
                  </Box>
                </>
              ) : null}
            </TimesWrapper> */}
          </Box>
        );
        day = moment(day).add(1, 'days');
      }
      rows.push(
        <Box className="row" key={day.format()}>
          {days}
        </Box>
      );

      days = [];
    }
    return (
      <div className="body">
        {rows}
        {/* {loading ? (
          <AvailabilitiesLoadingErrorWrapper>
            <SpinLoader />
            <AvailabilitiesLoadingErrorText>
              Loading Availabilities
            </AvailabilitiesLoadingErrorText>
          </AvailabilitiesLoadingErrorWrapper>
        ) : null}
        {error ? (
          <AvailabilitiesLoadingErrorWrapper>
            <AvailabilitiesLoadingErrorText className="error title">
              An error occured
            </AvailabilitiesLoadingErrorText>
            <AvailabilitiesLoadingErrorText className="error">
              Unable to retrieve availabilities
            </AvailabilitiesLoadingErrorText>
          </AvailabilitiesLoadingErrorWrapper>
        ) : null} */}
      </div>
    );
  };

  const onDateClick = (day: string) => {
    setSelectedDate(day !== selectedDate ? day : null)
  };

  const nextMonth = () => {
    setCurrentMonth(moment(currentMonth).add(1, 'months').toDate())
  };

  const prevMonth = () => {
    setCurrentMonth(moment(currentMonth).add(-1, 'months').toDate())
  };

  return (
    <Flex flexDirection={'column'} maxWidth='280px' mb={[3, 0, 0]}>
      {!!showTitle && (
        <Heading style={{ fontSize: '24px' }} mb={3}>Your Calendar</Heading>
      )}
      <div className='calendarMin'>
        <Card style={{ borderRadius: '20px', paddingBottom: '20px' }}>
          {renderHeader()}
          {renderDays()}

          {renderCells()}
          {/* (data, loading, error, selectedDate)} */}
        </Card>
      </div>
    </Flex>
  );
}

export default MinimalCalendarGrid;
